import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import useAPI from '../../hooks/useAPI';
import OtpInput from '../../components/otp-input';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (response?: any) => void;
  isSwitching?: boolean;
  isAlreadySetUp?: boolean;
  existingPhoneNumber?: string;
  enableCompanyMfaAfterSetup?: boolean;
  email?: string; // Optional email for login-time setup (no auth required)
}

/** Fallback: country from browser locale (e.g. en-CA -> ca). */
function getCountryFromLocale(): string {
  try {
    const locale = typeof navigator !== 'undefined' && (navigator.language || (navigator as any).userLanguage);
    if (locale && locale.includes('-')) {
      const country = locale.split('-')[1]?.toLowerCase();
      if (country && country.length === 2) return country;
    }
    if (typeof navigator !== 'undefined' && navigator.languages?.[0]) {
      const first = navigator.languages[0];
      if (first.includes('-')) {
        const country = first.split('-')[1]?.toLowerCase();
        if (country && country.length === 2) return country;
      }
    }
  } catch (_) {}
  return 'ca';
}

/** Fetch country code from IP geolocation (reflects user location). Falls back to locale then "ca". */
async function fetchCountryFromIp(): Promise<string> {
  try {
    const res = await fetch('https://ipapi.co/json/', { method: 'GET' });
    if (!res.ok) return getCountryFromLocale();
    const data = await res.json();
    const code = data?.country_code;
    if (typeof code === 'string' && code.length === 2) return code.toLowerCase();
  } catch (_) {}
  return getCountryFromLocale();
}

const SmsSetupDialog: React.FC<Props> = ({ open, onClose, onSuccess, isSwitching = false, isAlreadySetUp = false, existingPhoneNumber, enableCompanyMfaAfterSetup = false, email }) => {
  const { t } = useTranslation();
  const API = useAPI();
  const [phoneNumber, setPhoneNumber] = useState(existingPhoneNumber || '');
  const [detectedCountry, setDetectedCountry] = useState<string>(() => getCountryFromLocale());
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchCountryFromIp().then((country) => {
      if (!cancelled) setDetectedCountry(country);
    });
    return () => { cancelled = true; };
  }, [open]);

  React.useEffect(() => {
    if (open && isSwitching && isAlreadySetUp && existingPhoneNumber && !codeSent) {
      // When switching and already set up, automatically send code to existing phone
      const autoSendCode = async () => {
        try {
          setSendingCode(true);
          setError(null);
          await API.setupSms(existingPhoneNumber);
          setCodeSent(true);
        } catch (err: any) {
          console.error('[SMS Setup] Error:', err);
          setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.sms_setup_error') || 'Failed to send verification code');
        } finally {
          setSendingCode(false);
        }
      };
      autoSendCode();
    }
  }, [open, isSwitching, isAlreadySetUp, existingPhoneNumber, codeSent]);

  const handleSendCode = async () => {
    if (!phoneNumber || phoneNumber.trim().length === 0) {
      setError(t('sentences:pages.mfa_setup.phone_required') || 'Phone number is required');
      return;
    }

    try {
      setSendingCode(true);
      setError(null);
      // If email is provided, use login-time endpoint (no auth required)
      await API.setupSms(phoneNumber, email);
      setCodeSent(true);
    } catch (err: any) {
      console.error('[SMS Setup] Error:', err);
      setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.sms_setup_error') || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async (codeOverride?: string) => {
    const codeToSend = codeOverride ?? verificationCode;
    if (codeToSend.length !== 6) return;
    setVerificationAttempted(true);

    try {
      setVerifying(true);
      setError(null);
      const response = await API.verifySms(codeToSend, email);
      
      // If this is login-time setup, the response includes accessToken and user
      // We should handle that in the parent component
      if (email && response.accessToken) {
        // Login-time setup completed - parent should handle the token
      }
      
      // If this is admin/owner setting up their method to enable company MFA, enable it now
      if (enableCompanyMfaAfterSetup) {
        try {
          await API.put(`companies/updateMfa`, { mfa: true });
        } catch (err: any) {
          console.error('[SMS Setup] Error enabling company MFA:', err);
          setError(err?.response?.data?.message || 'Failed to enable company MFA');
          setVerifying(false);
          return;
        }
      }
      
      // Pass response to onSuccess callback (for login-time setup)
      onSuccess(response);
      onClose();
    } catch (err: any) {
      console.error('[SMS Setup] Verification error:', err);
      setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.sms_verify_error') || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    if (!isSwitching || !isAlreadySetUp) {
      setPhoneNumber('');
    }
    setCodeSent(false);
    setVerificationCode('');
    setError(null);
    setVerificationAttempted(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: 3 },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          pb: 0,
          pt: 2,
          px: 2.5,
        }}
      >
        {isSwitching
          ? (t('sentences:pages.mfa_setup.sms_switch_title') || 'Set up SMS')
          : (t('sentences:pages.mfa_setup.sms_title') || 'Set up 2FA with SMS')}
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 1.25, pb: 1 }}>
        {error && verificationAttempted && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => {
              setError(null);
              setVerificationAttempted(false);
            }}
          >
            {error}
          </Alert>
        )}

        {!codeSent && (
          !isSwitching || !isAlreadySetUp ? (
            <Typography variant="body2" sx={{ mb: 2, mt:2 , color: 'text.secondary', fontSize: '0.8125rem' }}>
              {t('sentences:pages.mfa_setup.sms_description') ||
                'Enter your phone number to receive a verification code via text message.'}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.8125rem' }}>
              {t('sentences:pages.mfa_setup.sms_switch_description') ||
                'A verification code has been sent to your phone. Enter it below to complete setup.'}
            </Typography>
          )
        )}

        {!codeSent ? (
          <Box sx={{
              '& .react-tel-input': { fontFamily: '"Maven Pro", sans-serif', position: 'relative' },
              '& .react-tel-input .form-control': {
                border: 'none',
                boxShadow: 'none',
                boxSizing: 'border-box',
                padding: '0 8px 0 70px !important',
                paddingTop: '0 !important',
                paddingBottom: '0 !important',
                lineHeight: '38px',
                height: '38px',
                marginTop: '-3px',
              },
              '& .react-tel-input .flag-dropdown': { border: 'none', borderRadius: 0 },
              '& .react-tel-input .special-label': { display: 'none' },
            }}>
            <PhoneInput
              country={detectedCountry}
              value={phoneNumber ? phoneNumber.replace(/^\+/, '') : ''}
              onChange={(value) => {
                setPhoneNumber(value ? `+${value}` : '');
                setError(null);
              }}
              priority={{ ca: 0, us: 1 }}
              preferredCountries={['ca', 'us']}
              disabled={sendingCode}
              containerClass="phone-input-sms"
              containerStyle={{
                width: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: 0,
                height: 40,
              }}
              inputStyle={{
                width: '100%',
                height: '38px',
                fontSize: '14px',
                color: '#1A2A3E',
                border: 'none',
                background: 'transparent',
                paddingLeft: 8,
                outline: 'none',
                lineHeight: '38px',
                paddingTop: 0,
                paddingBottom: 0,
                margin: 0,
              }}
              buttonStyle={{
                height: 38,
                minWidth: 46,
                padding: '0 6px',
                border: 'none',
                borderRight: '1px solid #e8e8e8',
                background: '#fafafa',
                borderRadius: '8px 0 0 8px',
              }}
              dropdownStyle={{
                fontFamily: '"Maven Pro", sans-serif',
                textAlign: 'left',
                fontSize: '13px',
              }}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, fontSize: '0.8125rem' }}>
              {t('sentences:pages.mfa_setup.sms_enter_code') || 'Enter the 6-digit code'}
            </Typography>
            <OtpInput
              length={6}
              value={verificationCode}
              onChange={(value) => {
                setVerificationCode(value);
                if (error) setError(null);
                setVerificationAttempted(false);
              }}
              disabled={verifying}
              error={false}
              onComplete={(value) => {
                if (value.length === 6) handleVerify(value);
                if (error) setError(null);
                setVerificationAttempted(false);
              }}
              onEnter={() => {
                if (verificationCode.length === 6 && !verifying) handleVerify();
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={sendingCode || verifying} color="inherit" sx={{ textTransform: 'none' }}>
          {t('verbs.cancel')}
        </Button>
        {!codeSent ? (
          <Button
            onClick={handleSendCode}
            variant="contained"
            disabled={!phoneNumber || sendingCode}
            sx={{ textTransform: 'none', minWidth: 120 }}
          >
            {sendingCode ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                {t('verbs.sending') || 'Sending…'}
              </>
            ) : (
              t('verbs.send_code') || 'Send code'
            )}
          </Button>
        ) : (
          <Button
            onClick={() => handleVerify()}
            variant="contained"
            disabled={verifying || verificationCode.length !== 6}
            sx={{ textTransform: 'none', minWidth: 100 }}
          >
            {verifying ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                {t('verbs.verifying') || 'Verifying…'}
              </>
            ) : (
              t('verbs.verify')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SmsSetupDialog;

